import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HiOutlineAcademicCap,
  HiOutlineBookOpen,
  HiOutlineClipboardList,
  HiOutlineChevronRight,
  HiOutlineMenu,
  HiOutlineSearch,
  HiOutlineFilter,
  HiOutlineOfficeBuilding,
  HiOutlineX,
  HiOutlineRefresh
} from 'react-icons/hi';
import { useSearch } from "../../Context/SearchContext";

const CoursesPage = () => {
  const navigate = useNavigate();
  const [selectedSection, setSelectedSection] = useState('ug');
  const { selectedEvent } = useSearch();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [coursesData, setCoursesData] = useState({
    ug: { title: '', items: [] },
    pg: { title: '', items: [] },
    admission: { title: 'ADMISSIONS', items: [] },
  });

  const searchInputRef = useRef(null);

  // ✅ UX FIX 1: Debounce search for better performance
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // ✅ UX FIX 2: Close mobile menu on escape key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [mobileMenuOpen]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { x: -20, opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  const listItemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.4, ease: "easeOut" }
    },
    hover: {
      x: 10,
      scale: 1.02,
      transition: { duration: 0.2 }
    }
  };

  const handleItemClick = (item, section) => {
    if (section === 'ug') {
      const id = item._id || item.Id || '';
      navigate(`/graduate-course-details/${id}`);
    } else if (section === 'pg') {
      const id = item._id || item.Id || '';
      navigate(`/postgraduate-course-details/${id}`);
    } else if (section === 'admission') {
      const id = item._id || item.Id || '';
      navigate(`/admission-details/${id}`);
    }
  };

  const fetchData = async (showRefresh = false) => {
    const token = Cookies.get('userToken');
    
    if (showRefresh) setRefreshing(true);

    try {
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      const exploreRes = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/apis/admin/getAllExportCourse`,
        config
      );
      const exploreData = exploreRes.data.data;

      const admissionItem = exploreData.find(item => item.exploreCourse === "Admission");
      const admissionId = admissionItem?._id;

      const [ugRes, admissionRes, pgRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/apis/user/getAllUnderGraduateCourse`, config),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/apis/user/getSingleAdmissionTypeThroughByExploreId/${admissionId}`, config),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/apis/user/getAllPostGraduateCourse`, config)
      ]);

      const exploreCourses = exploreRes.data.data;
      const ugCourses = ugRes.data.data;
      const admissionMenus = admissionRes.data.data;
      const pgCourses = pgRes.data.data;

      const ugExplore = exploreCourses.find((c) => c.exploreCourse.toLowerCase().includes('under graduate'));
      const pgExplore = exploreCourses.find((c) => c.exploreCourse.toLowerCase().includes('post graduate'));

      setCoursesData({
        ug: {
          title: ugExplore?.exploreCourse || 'Under Graduate',
          items: ugCourses.map((course) => ({
            name: course.courseName,
            _id: course._id || '',
            collegeName: course.adminId?.collegeName || 'Unknown College'
          })),
        },
        pg: {
          title: pgExplore?.exploreCourse || 'Post Graduate',
          items: pgCourses.map((course) => ({
            name: course.courseName,
            _id: course._id || '',
            collegeName: course.adminId?.collegeName || 'Unknown College'
          })),
        },
        admission: {
          title: 'ADMISSIONS',
          items: admissionMenus.map((ad) => ({
            name: ad.admissionName,
            _id: ad._id || '',
            collegeName: ad.adminId?.collegeName || 'Unknown College'
          })),
        },
      });

      if (selectedEvent?.type) {
        const type = selectedEvent.type.toLowerCase();
        if (type === 'admissiontype') {
          setSelectedSection('admission');
        } else if (type === 'undergraduate' || type === 'ug') {
          setSelectedSection('ug');
        } else if (type === 'postgraduate' || type === 'pg') {
          setSelectedSection('pg');
        }
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedEvent]);

  const navItems = [
    { key: 'ug', label: coursesData.ug.title || 'Under Graduate', icon: HiOutlineAcademicCap },
    { key: 'pg', label: coursesData.pg.title || 'Post Graduate', icon: HiOutlineBookOpen },
    { key: 'admission', label: 'ADMISSIONS', icon: HiOutlineClipboardList },
  ];

  // Filter items based on debounced search
  const filteredItems = coursesData[selectedSection]?.items.filter(item =>
    item.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
    item.collegeName.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
  ) || [];

  const getSectionIcon = (section) => {
    switch (section) {
      case 'ug': return HiOutlineAcademicCap;
      case 'pg': return HiOutlineBookOpen;
      case 'admission': return HiOutlineClipboardList;
      default: return HiOutlineAcademicCap;
    }
  };

  const SectionIcon = getSectionIcon(selectedSection);

  const clearSearch = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    searchInputRef.current?.focus();
  };

  const renderSidebarSkeleton = () => (
    <div className="space-y-2 p-3">
      {[...Array(3)].map((_, i) => (
        <div
          key={i}
          className="h-14 bg-gray-200 rounded-xl animate-pulse"
        />
      ))}
    </div>
  );

  const renderContentSkeleton = () => (
    <div className="space-y-3">
      {[...Array(5)].map((_, i) => (
        <div
          key={i}
          className="h-20 bg-gray-200 rounded-xl animate-pulse"
        />
      ))}
    </div>
  );

  const EmptyState = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="text-center py-16 bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200"
    >
      <div className="w-20 h-20 mx-auto bg-gradient-to-r from-gray-100 to-gray-200 rounded-full flex items-center justify-center mb-4">
        <HiOutlineSearch className="w-10 h-10 text-gray-500" />
      </div>
      <h3 className="text-xl font-semibold text-gray-700 mb-2">
        {debouncedSearchTerm ? 'No items found' : 'No courses available'}
      </h3>
      <p className="text-gray-500 text-sm max-w-md mx-auto mb-6">
        {debouncedSearchTerm 
          ? `No items matching "${debouncedSearchTerm}"`
          : 'There are no courses available in this category yet.'}
      </p>
      {debouncedSearchTerm && (
        <button
          onClick={clearSearch}
          className="px-6 py-3 bg-gray-800 text-white rounded-xl hover:bg-gray-900 transition inline-flex items-center gap-2"
        >
          <HiOutlineX className="w-5 h-5" />
          Clear Search
        </button>
      )}
    </motion.div>
  );

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="relative max-w-7xl mx-auto p-4 sm:p-6 lg:p-8"
    >
      {/* Mobile Menu Toggle */}
      <div className="lg:hidden mb-4">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="w-full bg-white rounded-xl shadow-md p-4 flex items-center justify-between border border-gray-200"
          aria-label="Toggle course menu"
          aria-expanded={mobileMenuOpen}
        >
          <div className="flex items-center space-x-2">
            <HiOutlineMenu className="w-5 h-5 text-gray-600" />
            <span className="font-semibold text-gray-700">Explore Courses Menu</span>
          </div>
          <motion.div
            animate={{ rotate: mobileMenuOpen ? 90 : 0 }}
            transition={{ duration: 0.3 }}
          >
            <HiOutlineChevronRight className="w-5 h-5 text-gray-500" />
          </motion.div>
        </motion.button>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black lg:hidden z-40"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}
      </AnimatePresence>

      <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
        {/* Sidebar */}
        <motion.div
          variants={itemVariants}
          className={`
            ${mobileMenuOpen ? 'fixed inset-y-0 left-0 z-50 w-72' : 'hidden'} 
            lg:block 
            w-full lg:w-72 
            bg-white/95 backdrop-blur-sm 
            rounded-2xl shadow-xl 
            overflow-hidden 
            border border-gray-200
            lg:sticky lg:top-4 lg:self-start
            transition-transform duration-300
          `}
        >
          {/* Sidebar Header */}
          <div className="bg-gradient-to-r from-gray-100 to-gray-200 p-5 border-b border-gray-200 sticky top-0 z-10">
            <div className="flex justify-between items-center">
              <h2 className="text-gray-700 font-bold text-lg flex items-center space-x-2">
                <HiOutlineFilter className="w-5 h-5 text-gray-600" />
                <span>Explore Courses</span>
              </h2>
              {mobileMenuOpen && (
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="lg:hidden p-2 hover:bg-gray-200 rounded-lg transition-colors"
                  aria-label="Close menu"
                >
                  <HiOutlineX className="w-5 h-5 text-gray-600" />
                </button>
              )}
            </div>
            <p className="text-gray-500 text-xs mt-1">Select a category to browse</p>
          </div>

          {/* Navigation Items */}
          {loading ? (
            renderSidebarSkeleton()
          ) : (
            <ul className="p-3 max-h-[calc(100vh-200px)] overflow-y-auto">
              {navItems.map((item, index) => {
                const Icon = item.icon;
                return (
                  <motion.li
                    key={item.key}
                    variants={itemVariants}
                    custom={index}
                    className="mb-2"
                  >
                    <motion.button
                      onClick={() => {
                        setSelectedSection(item.key);
                        setMobileMenuOpen(false);
                      }}
                      whileHover={{ scale: 1.02, x: 5 }}
                      whileTap={{ scale: 0.98 }}
                      className={`
                        w-full text-left px-4 py-3 
                        rounded-xl transition-all duration-300
                        flex items-center space-x-3
                        ${selectedSection === item.key
                          ? 'bg-gray-700 text-white shadow-lg'
                          : 'hover:bg-gray-100 text-gray-700'}
                      `}
                      aria-current={selectedSection === item.key ? 'page' : undefined}
                    >
                      <div className={`
                        p-2 rounded-lg
                        ${selectedSection === item.key
                          ? 'bg-white/20'
                          : 'bg-gray-100'}
                      `}>
                        <Icon className={`w-4 h-4 ${selectedSection === item.key ? 'text-white' : 'text-gray-600'}`} />
                      </div>
                      <span className="flex-1 font-medium">{item.label}</span>
                      {selectedSection === item.key && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ duration: 0.3 }}
                        >
                          <HiOutlineChevronRight className="w-5 h-5" />
                        </motion.div>
                      )}
                    </motion.button>
                  </motion.li>
                );
              })}
            </ul>
          )}

          {/* Sidebar Footer Stats */}
          {!loading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="border-t border-gray-200 p-4 bg-gray-50/50 sticky bottom-0"
            >
              <div className="text-xs text-gray-500 mb-2">Quick Stats</div>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-white rounded-lg p-2 text-center border border-gray-200">
                  <div className="text-lg font-bold text-gray-700">{coursesData.ug.items.length}</div>
                  <div className="text-xs text-gray-500">UG Courses</div>
                </div>
                <div className="bg-white rounded-lg p-2 text-center border border-gray-200">
                  <div className="text-lg font-bold text-gray-700">{coursesData.pg.items.length}</div>
                  <div className="text-xs text-gray-500">PG Courses</div>
                </div>
              </div>
            </motion.div>
          )}
        </motion.div>

        {/* Main Content */}
        <motion.div
          variants={itemVariants}
          className="flex-1 min-w-0"
        >
          {/* Section Header with Search */}
          <motion.div
            variants={itemVariants}
            className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl p-5 mb-6 border border-gray-200"
          >
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-gray-100 to-gray-200 rounded-xl flex items-center justify-center border border-gray-200">
                  <SectionIcon className="w-5 h-5 text-gray-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-700">
                    {coursesData[selectedSection]?.title}
                  </h3>
                  <p className="text-xs text-gray-500">
                    {filteredItems.length} {filteredItems.length === 1 ? 'item' : 'items'} available
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 w-full sm:w-auto">
                {/* Search Bar */}
                <div className="relative flex-1 sm:w-64">
                  <HiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search courses or colleges..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-300 focus:border-transparent transition-all duration-200 text-gray-700 placeholder-gray-400"
                  />
                  {searchTerm && (
                    <button
                      onClick={clearSearch}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      aria-label="Clear search"
                    >
                      <HiOutlineX className="w-5 h-5" />
                    </button>
                  )}
                </div>

                {/* Refresh button (optional) */}
                <button
                  onClick={() => fetchData(true)}
                  disabled={refreshing}
                  className="p-2.5 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-100 transition disabled:opacity-50"
                  aria-label="Refresh"
                >
                  <HiOutlineRefresh className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>
          </motion.div>

          {/* Results count */}
          {!loading && filteredItems.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mb-3 px-2"
            >
              <p className="text-sm text-gray-500">
                Showing {filteredItems.length} {filteredItems.length === 1 ? 'result' : 'results'}
              </p>
            </motion.div>
          )}

          {/* Content Area */}
          <AnimatePresence mode="wait">
            {loading ? (
              <motion.div
                key="skeleton"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {renderContentSkeleton()}
              </motion.div>
            ) : filteredItems.length === 0 ? (
              <EmptyState />
            ) : (
              <motion.div
                key="content"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-3"
              >
                {filteredItems.map((item, index) => (
                  <motion.div
                    key={`${item._id}-${index}`}
                    variants={listItemVariants}
                    initial="hidden"
                    animate="visible"
                    whileHover="hover"
                    custom={index}
                    layout
                  >
                    <div
                      onClick={() => handleItemClick(item, selectedSection)}
                      className="group relative px-5 py-4 bg-white rounded-xl shadow-sm border border-gray-200 cursor-pointer transition-all duration-300 hover:shadow-md hover:border-gray-300 active:scale-95 overflow-hidden"
                      role="button"
                      tabIndex={0}
                      onKeyPress={(e) => e.key === 'Enter' && handleItemClick(item, selectedSection)}
                    >
                      {/* Hover Background Effect */}
                      <motion.div
                        className="absolute inset-0 bg-gray-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                        initial={false}
                      />

                      <div className="relative">
                        {/* Course Name */}
                        <div className="flex items-center justify-between">
                          <span className="text-gray-700 font-medium group-hover:text-gray-900 transition-colors duration-300">
                            {item.name}
                          </span>

                          <motion.div
                            initial={{ x: -10, opacity: 0 }}
                            whileHover={{ x: 0, opacity: 1 }}
                            transition={{ duration: 0.2 }}
                          >
                            <HiOutlineChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors duration-300" />
                          </motion.div>
                        </div>

                        {/* College Name */}
                        <div className="flex items-center space-x-2 mt-2">
                          <div className="w-5 h-5 bg-gray-100 rounded-full flex items-center justify-center">
                            <HiOutlineOfficeBuilding className="w-3 h-3 text-gray-500" />
                          </div>
                          <span className="text-xs text-gray-500 font-medium">
                            by <span className="text-gray-600">{item.collegeName}</span>
                          </span>
                        </div>

                        {/* Decorative element */}
                        <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-gradient-to-b from-gray-300 to-gray-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      </div>

                      {/* Bottom Border Animation */}
                      <motion.div
                        className="absolute bottom-0 left-0 h-0.5 bg-gray-400"
                        initial={{ width: 0 }}
                        whileHover={{ width: '100%' }}
                        transition={{ duration: 0.3 }}
                      />
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default CoursesPage;